import { Box, Stack, Tab, Tabs } from "@mui/material";
import { useSearchParams } from "Navigation/useSearchParams";
import type { ReactNode } from "react";

export type TabItem = {
  id: string;
  label: ReactNode;
  content: ReactNode;
  isHidden?: boolean;
};

type Props = {
  tabs: Array<TabItem>;
  /** Hides the scroll arrows; overflowing tabs stay reachable by swipe/drag. */
  hideScrollButtons?: boolean;
};

/**
 * Tabs synced with the "activeTab" query parameter, so a tab is deep-linkable
 * (e.g. ?activeTab=publicInformation) and survives back-navigation.
 */
export const QueryParamTabs = ({ tabs, hideScrollButtons }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleTabs = tabs.filter(({ isHidden }) => !isHidden);
  const urlTabId = searchParams.get("activeTab");
  const isValidTab = urlTabId && visibleTabs.some(({ id }) => id === urlTabId);
  const activeTab = isValidTab ? urlTabId : visibleTabs[0]?.id || false;

  return (
    <Stack>
      <Tabs
        value={activeTab}
        // Functional form merges instead of replacing: unrelated params (e.g. the People
        // page's ?search= term) survive a tab switch.
        onChange={(_e, id) =>
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("activeTab", id);
            return next;
          })
        }
        variant="scrollable"
        scrollButtons={hideScrollButtons ? false : "auto"}
        allowScrollButtonsMobile={!hideScrollButtons}
        sx={{
          width: "100%",
          "& .MuiTabs-scrollButtons.Mui-disabled": {
            opacity: 0.3,
          },
        }}
      >
        {visibleTabs.map(({ id, label }) => (
          <Tab key={id} value={id} label={label} />
        ))}
      </Tabs>
      <Box mt="0.5rem">
        {visibleTabs.map(({ id, content }) => (
          <div role="tabpanel" hidden={id !== activeTab} key={id}>
            {id === activeTab && content}
          </div>
        ))}
      </Box>
    </Stack>
  );
};
